import { useState } from "react"
import { router } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { SectionHeader } from "@/components/ui/headers"
import { formatDateTime } from "@/lib/formatters"
import { t } from "@/lib/i18n"

/**
 * The restaurant's own customers, and the points they hold here.
 *
 * **"Aquí" is the whole feature.** A person who eats at two restaurants has two
 * rows, and neither restaurant can reach the other's — the server never looks a
 * customer up by anything but `current_location.customers`. Nothing on this
 * screen hints that a diner exists elsewhere, because from this restaurant's
 * side they do not.
 *
 * The outstanding balance is stated once, at the top. A restaurant running a
 * points programme is carrying a debt it will be asked to honour, and the screen
 * that creates that debt should say how big it is.
 *
 * Points are moved by a signed action, never by editing a balance: the balance
 * is derived from an append-only ledger, and a field that set it directly would
 * put the two out of step with no way to tell which was right.
 */

export type Customer = {
  id: number
  name: string | null
  phone: string | null
  email: string | null
  pointsBalance: number
  linkedToAccount: boolean
}

export type LedgerEntry = {
  id: number
  customerId: number
  customerName: string | null
  points: number
  balanceAfter: number
  reason: string
  reasonLabel: string
  note: string | null
  createdAt: string
}

function tCustomers(key: string, options: Record<string, string | number> = {}) {
  return t(`ui.react.pages.staff.location.manage.customers.index.${key}`, options)
}

function PointsControl({ customer }: { customer: Customer }) {
  const [points, setPoints] = useState("")

  const move = (direction: "earn" | "redeem") => {
    router.patch(
      `/manage/customers/${customer.id}/adjust_points`,
      { points, direction },
      { preserveScroll: true, onSuccess: () => setPoints("") }
    )
  }

  return (
    <span className="flex items-center gap-2">
      <input
        className="border-input h-11 w-20 rounded-md border px-3 text-sm tabular-nums md:h-9"
        inputMode="numeric"
        value={points}
        placeholder={tCustomers("points_placeholder")}
        onChange={(event) => setPoints(event.target.value)}
        data-testid={`customer-points-input-${customer.id}`}
        aria-label={tCustomers("points_label", { name: customer.name ?? "" })}
      />

      <Button
        variant="secondary"
        className="h-11 md:h-9"
        disabled={points.trim() === ""}
        data-testid={`customer-earn-${customer.id}`}
        onClick={() => move("earn")}
      >
        {tCustomers("earn")}
      </Button>

      <Button
        variant="secondary"
        className="h-11 md:h-9"
        // Redeeming more than the balance is refused by the server; the button
        // stays live so the refusal is a sentence rather than a dead control the
        // operator has to reverse-engineer.
        disabled={points.trim() === "" || customer.pointsBalance === 0}
        data-testid={`customer-redeem-${customer.id}`}
        onClick={() => move("redeem")}
      >
        {tCustomers("redeem")}
      </Button>
    </span>
  )
}

export function CustomerLedger({
  customers,
  recentEntries,
  outstandingPoints,
  timeZone,
}: {
  customers: Customer[]
  recentEntries: LedgerEntry[]
  outstandingPoints: number
  timeZone: string
}) {
  const [name, setName] = useState("")
  const [phone, setPhone] = useState("")

  return (
    <>
      <Card density="compact" data-testid="customer-card">
        <CardHeader className="border-b">
          <CardTitle>{tCustomers("title")}</CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* The liability, stated once and up front. */}
          <p
            className="text-foreground text-sm font-semibold tabular-nums"
            data-testid="customer-outstanding"
          >
            {tCustomers("outstanding", { points: outstandingPoints })}
          </p>

          {customers.length === 0 ? (
            <p className="text-muted-foreground text-sm" data-testid="customer-empty">
              {tCustomers("empty")}
            </p>
          ) : (
            <ul className="divide-border divide-y rounded-md border" data-testid="customer-list">
              {customers.map((customer) => (
                <li
                  key={customer.id}
                  className="flex flex-wrap items-center gap-3 px-4 py-3"
                  data-testid={`customer-${customer.id}`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">
                      {customer.name ?? customer.phone ?? customer.email}
                    </span>
                    <span className="text-muted-foreground block truncate text-xs tabular-nums">
                      {tCustomers("balance", { points: customer.pointsBalance })}
                      {customer.linkedToAccount ? ` · ${tCustomers("linked")}` : ""}
                    </span>
                  </span>

                  <PointsControl customer={customer} />
                </li>
              ))}
            </ul>
          )}

          <form
            className="flex flex-wrap items-end gap-2"
            data-testid="customer-create"
            onSubmit={(event) => {
              event.preventDefault()
              router.post(
                "/manage/customers",
                { name, phone },
                { preserveScroll: true, onSuccess: () => { setName(""); setPhone("") } }
              )
            }}
          >
            <label className="flex min-w-40 flex-1 flex-col gap-1">
              <span className="text-muted-foreground text-xs">{tCustomers("name_label")}</span>
              <input
                className="border-input h-11 rounded-md border px-3 text-sm md:h-9"
                value={name}
                onChange={(event) => setName(event.target.value)}
                data-testid="customer-name-input"
              />
            </label>

            <label className="flex w-40 flex-col gap-1">
              <span className="text-muted-foreground text-xs">{tCustomers("phone_label")}</span>
              <input
                className="border-input h-11 rounded-md border px-3 text-sm tabular-nums md:h-9"
                inputMode="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                data-testid="customer-phone-input"
              />
            </label>

            <Button
              type="submit"
              className="h-11 md:h-9"
              disabled={name.trim() === "" && phone.trim() === ""}
              data-testid="customer-submit"
            >
              {tCustomers("add")}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card density="compact" data-testid="ledger-card">
        <CardHeader className="border-b">
          <CardTitle>{tCustomers("ledger_title")}</CardTitle>
        </CardHeader>

        <CardContent className="space-y-2">
          <SectionHeader title={tCustomers("ledger_subtitle")} className="px-0" />

          {recentEntries.length === 0 ? (
            <p className="text-muted-foreground text-sm" data-testid="ledger-empty">
              {tCustomers("ledger_empty")}
            </p>
          ) : (
            <ul className="divide-border divide-y rounded-md border" data-testid="ledger-list">
              {recentEntries.map((entry) => (
                <li
                  key={entry.id}
                  className="flex flex-wrap items-center justify-between gap-2 px-4 py-2"
                  data-testid={`ledger-entry-${entry.id}`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{entry.customerName}</span>
                    <span className="text-muted-foreground block truncate text-xs tabular-nums">
                      {entry.reasonLabel} · {formatDateTime(entry.createdAt, { timeZone })}
                    </span>
                  </span>

                  {/* The sign carries the meaning, and the figure stays neutral
                      per the dot rule — no green for earning, no red for
                      spending. */}
                  <span className="text-foreground shrink-0 text-sm font-semibold tabular-nums">
                    {entry.points > 0 ? `+${entry.points}` : entry.points}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </>
  )
}
